import { of as observableOf, forkJoin as observableForkJoin } from "rxjs";

import { catchError, map } from "rxjs/operators";
import { Component, OnInit, ViewChild } from "@angular/core";
import { KpiService } from "../kpi/kpi.service";
import { Title } from "@angular/platform-browser";

import * as _ from "lodash";
import { ActivatedRoute, Router } from "@angular/router";
import { OOPS_MESSAGES } from "../constants";
import {
  Dashboard,
  DistrictSchools,
  SchoolKpiData,
  UserInfo,
} from "../shared/interfaces";

@Component({
  selector: "school-comparison",
  templateUrl: "./school-comparison.component.html",
  styleUrls: ["./school-comparison.component.scss"],
})
export class SchoolComparisonComponent implements OnInit {
  schools;
  copyOfSchools;
  copyOfSchoolsWithoutKPIs;
  schoolFiltersForDropdown;
  schoolRegionsForDropdown;
  schoolLevelsForDropdown;
  demoCategoriesForDropdown;
  demographicCategoryNamesList;
  userSchools;
  district;
  districtOverall = {};
  districtSchoolLevels = [];
  districtSchoolRegions = [];
  districtColors = [];
  districtColorsOverall = [];
  districtColorsSchoolLevels = [];
  districtColorsSchoolRegions = [];

  kpis;
  columns = [];
  panels;
  sortCol;
  sortDirection;

  initialPageNumber;
  initialPageSize;
  searchText: string;

  @ViewChild("schoolFiltersDropdown") schoolFiltersDropdown;
  @ViewChild("schoolLevelsDropdown") schoolLevelsDropdown;
  @ViewChild("schoolRegionsDropdown") schoolRegionsDropdown;
  @ViewChild("demoDropdown") demoDropdown;

  constructor(
    public kpiService: KpiService,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    // Clear error messages
    this.kpiService.errorMessage = null;
    let urlParamMap = this.activatedRoute.snapshot.queryParamMap;
    if (
      _.has(urlParamMap, "params") &&
      urlParamMap["params"] &&
      _.keys(urlParamMap["params"]) &&
      _.keys(urlParamMap["params"]).length > 0
    ) {
      // we have url parameters in our route
      let selectedSchoolFilter = urlParamMap.get("filter");
      let selectedSchoolLevel = urlParamMap.get("level");
      let selectedSchoolRegion = urlParamMap.get("region");
      let selectedSchoolDemog = {
        id: urlParamMap.get("demographic"),
        text: urlParamMap.get("demographic"),
      };
      this.initialPageNumber = urlParamMap.get("pageNumber")
        ? parseInt(urlParamMap.get("pageNumber"))
        : null;
      this.initialPageSize = urlParamMap.get("pageSize")
        ? parseInt(urlParamMap.get("pageSize"))
        : null;

      if (
        selectedSchoolFilter &&
        selectedSchoolFilter != this.kpiService.selectedSchoolFilter
      ) {
        this.kpiService.selectedSchoolFilter = selectedSchoolFilter;
      }
      if (
        selectedSchoolLevel &&
        selectedSchoolLevel != this.kpiService.selectedSchoolLevel
      ) {
        this.kpiService.selectedSchoolLevel = selectedSchoolLevel;
      }
      if (
        selectedSchoolRegion &&
        selectedSchoolRegion != this.kpiService.selectedSchoolRegion
      ) {
        this.kpiService.selectedSchoolRegion = selectedSchoolRegion;
      }
      if (
        selectedSchoolDemog &&
        !_.isEqual(
          selectedSchoolDemog,
          this.kpiService.selectedSchoolDemographic
        )
      ) {
        this.kpiService.selectedSchoolDemographic = selectedSchoolDemog;
      }
      if (
        this.initialPageNumber &&
        this.initialPageNumber != this.kpiService.schoolPageNumber
      ) {
        this.kpiService.schoolPageNumber = this.initialPageNumber;
      }
      if (
        this.initialPageSize &&
        this.initialPageSize != this.kpiService.schoolPageSize
      ) {
        this.kpiService.schoolPageSize = this.initialPageSize;
      }
    } else if (
      this.kpiService.schoolPageNumber ||
      this.kpiService.schoolPageSize ||
      (this.kpiService.selectedSchoolFilter &&
        (this.kpiService.selectedSchoolLevel ||
          this.kpiService.selectedSchoolRegion ||
          (this.kpiService.selectedSchoolDemographic &&
            this.kpiService.selectedSchoolDemographic.id)))
    ) {
      // we have filters in our store, so update the url params
      this.updateUrlParams();
    }
  }

  updateUrlParams() {
    let queryParams = { ...this.activatedRoute.snapshot.queryParams };
    queryParams = {
      ...queryParams,
      filter: this.kpiService.selectedSchoolFilter,
      level: this.kpiService.selectedSchoolLevel,
      region: this.kpiService.selectedSchoolRegion,
      demographic: this.kpiService.selectedSchoolDemographic
        ? this.kpiService.selectedSchoolDemographic.id
        : null,
      pageNumber: this.kpiService.schoolPageNumber,
      pageSize: this.kpiService.schoolPageSize,
    };
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
    });
  }

  updatePaginationUrlParams(event) {
    // update pageNumber / pageSize in kpiService
    this.kpiService.schoolPageNumber = event.pageIndex;
    this.kpiService.schoolPageSize = event.pageSize;
    // update url parameters
    this.updateUrlParams();
  }

  ngOnInit() {
    this.kpiService.loadingSchoolData = true;
    this.getSchoolsDataForUser();
  }

  getSchoolsDataForUser() {
    let calls = [];
    calls.push(this.getUserSchools());
    calls.push(this.getSchoolsForDistrict());
    observableForkJoin(calls).subscribe(
      (data) => {
        _.forEach(this.schools, (school) => {
          school["hasPermissionToView"] =
            this.userSchools[school.systemSchoolId] !== undefined;
        });
        this.getMetadataAndAssignTableVars();
      },
      (err) => {
        console.log(err);
        this.kpiService.loadingSchoolData = false;
      }
    );
  }

  getUserSchools() {
    return this.kpiService.getUserInfo().pipe(
      map(
        (data: UserInfo) => {
          this.userSchools = _.keyBy(data.schools, "systemSchoolId");
          this.kpiService.title = data.customer_name;
        },
        (error) => {
          this.kpiService.title = "School Comparison";
          console.error(error);
        }
      )
    );
  }

  getSchoolsForDistrict() {
    return this.kpiService.getDistrictSchools().pipe(
      map(
        (data: DistrictSchools) => {
          this.schools = _.keyBy(data.schools, "systemSchoolId");
          this.districtOverall = {
            officialSchoolName: {
              title: "District Overall",
              goalTitle: "District Goal",
            },
          };
          //Use new school_demographics list if present.  Fall back to old demographics list.
          let school_demographics = data.school_demographics
            ? data.school_demographics
            : data.demographics;
          this.populateSchoolFilterValues(
            data.schoolRegions,
            data.schoolLevels,
            school_demographics
          );
        },
        (error) => {
          console.error(error);
        }
      ),
      catchError((error) => observableOf({}))
    );
  }

  getMetadataAndAssignTableVars() {
    return this.kpiService.getSchoolComparisonMetadata().subscribe(
      (data: Dashboard) => {
        this.panels = _.filter(
          data.customerFramework.customerPanels,
          function (panel) {
            return panel["tiles"] && panel["tiles"].length > 0;
          }
        );
        let tiles = _.map(data.customerFramework.customerPanels, "tiles");
        this.kpis = _.flatten(tiles);
        this.columns = _.map(this.kpis, (kpi: any) => {
          return { title: kpi.title, id: String(kpi.KPI) };
        });
        this.districtColorsOverall = _.union(
          ["#4a4a4a"],
          _.map(this.kpis, "overallColor")
        );
        //Copy schools now, for future demographic filtering
        this.copyOfSchoolsWithoutKPIs = _.cloneDeep(this.schools);
        // once we have the list of kpis, we want to grab the kpi data for each kpi
        let chartDataResults = [];
        _.forEach(this.kpis, (kpi) => {
          chartDataResults.push(this.getKpiChartData(kpi));
        });
        chartDataResults.push(this.getAllKpiDataForSchools());
        observableForkJoin(chartDataResults).subscribe(
          (result) => {
            // All chart data has been loaded by school id.  Now sort schools by name.
            this.schools = _.sortBy(this.schools, [
              function (school) {
                return school.officialSchoolName;
              },
            ]);
            //Copy original schools table to support filtering
            this.copyOfSchools = _.cloneDeep(this.schools);
            //Set initial district data row
            this.district = this.districtOverall;
            this.districtColors = this.districtColorsOverall;
            if (
              this.kpiService.selectedSchoolFilter &&
              this.kpiService.selectedSchoolDemographic &&
              this.kpiService.selectedSchoolDemographic.id
            ) {
              this.getDemoKpiDataForSchools(
                this.kpiService.selectedSchoolFilter,
                this.kpiService.selectedSchoolDemographic.id
              );
            } else if (
              this.kpiService.selectedSchoolFilter &&
              this.kpiService.selectedSchoolLevel
            ) {
              this.filterSchoolsAndSetDistrictRowBySchoolLevel(true);
              this.kpiService.loadingSchoolData = false;
            } else if (
              this.kpiService.selectedSchoolFilter &&
              this.kpiService.selectedSchoolRegion
            ) {
              this.filterSchoolsAndSetDistrictRowBySchoolRegion(true);
              this.kpiService.loadingSchoolData = false;
            } else {
              this.kpiService.loadingSchoolData = false;
            }
          },
          (err) => {
            console.log(err);
            this.kpiService.loadingSchoolData = false;
          }
        );
      },
      (error) => {
        this.kpiService.showError(
          error,
          OOPS_MESSAGES.ACCESS_CONTENT,
          OOPS_MESSAGES.RENDER_DASHBOARD
        );
        this.kpiService.loadingSchoolData = false;
      }
    );
  }

  getKpiChartData(kpi) {
    return this.kpiService.getKPIData(kpi.KPI).pipe(
      map(
        (kpidata) => {
          // we add the new data for the district
          this.setDistrictKpiData(kpidata);
        },
        (error) => {
          console.log(error);
        }
      ),
      catchError((error) => observableOf({}))
    );
  }

  setDistrictKpiData(kpidata) {
    this.districtColorsOverall[String(kpidata.kpiId)] = kpidata.overallColor;
    this.districtOverall[String(kpidata.kpiId)] = {
      ...this.districtOverall[String(kpidata.kpiId)],
      overall: kpidata.overall,
      overallSuffix: kpidata.dataSuffix,
      overallPrefix: kpidata.dataPrefix,
      definition: kpidata.definition,
      goal: kpidata.goalText,
      upIsGood: kpidata.upIsGood,
      goalNumeric: kpidata.goalNumeric,
      roundTo: kpidata.roundTo,
    };
    if (
      kpidata.charts &&
      kpidata.charts.overall &&
      kpidata.charts.overall.chartData
    ) {
      this.districtOverall[String(kpidata.kpiId)] = {
        ...this.districtOverall[String(kpidata.kpiId)],
        overallSubtitle: kpidata.charts.overall.subtitle,
        descriptor: kpidata.charts.overall.chartData[0].descriptor,
        numerator: kpidata.charts.overall.chartData[0].numerator,
        denominator: kpidata.charts.overall.chartData[0].denominator,
      };
    }
    if (kpidata.charts && kpidata.charts.topBottomRank) {
      this.districtOverall[String(kpidata.kpiId)] = {
        ...this.districtOverall[String(kpidata.kpiId)],
        schoolSubtitle: kpidata.charts.topBottomRank.subtitle,
      };
    }

    if (
      kpidata.charts.characteristic.chartData &&
      kpidata.charts.characteristic.chartData["School Level"]
    ) {
      let schoolLevels =
        kpidata.charts.characteristic.chartData["School Level"];
      let schoolLevelData = schoolLevels.data;
      let districtSchoolLevels = this.districtSchoolLevels;
      let districtColorsSchoolLevels = this.districtColorsSchoolLevels;
      _.forEach(schoolLevelData, (schoolLevelValue) => {
        if (!districtSchoolLevels[schoolLevelValue.name]) {
          districtSchoolLevels[schoolLevelValue.name] = [];
          districtSchoolLevels[schoolLevelValue.name]["officialSchoolName"] = {
            title: schoolLevelValue.name + " School Overall",
            goalTitle: "District Goal",
          };
        }
        districtSchoolLevels[schoolLevelValue.name][String(kpidata.kpiId)] = {
          overall: schoolLevelValue.y,
          overallSuffix: kpidata.dataSuffix,
          overallPrefix: kpidata.dataPrefix,
          overallSubtitle: kpidata.charts.overall.subtitle,
          descriptor: schoolLevelValue.descriptor,
          numerator: schoolLevelValue.numerator,
          denominator: schoolLevelValue.denominator,
          definition: kpidata.definition,
          goal: kpidata.goalText,
          schoolSubtitle: kpidata.charts.topBottomRank.subtitle,
          upIsGood: kpidata.upIsGood,
          goalNumeric: kpidata.goalNumeric,
          roundTo: kpidata.roundTo,
        };

        if (!districtColorsSchoolLevels[schoolLevelValue.name]) {
          districtColorsSchoolLevels[schoolLevelValue.name] = [];
        }
        districtColorsSchoolLevels[schoolLevelValue.name][
          String(kpidata.kpiId)
        ] = schoolLevelValue.color;
      });
    }

    if (
      kpidata.charts.characteristic.chartData &&
      kpidata.charts.characteristic.chartData["School Region"]
    ) {
      let schoolRegions =
        kpidata.charts.characteristic.chartData["School Region"];
      let schoolRegionData = schoolRegions.data;
      let districtSchoolRegions = this.districtSchoolRegions;
      let districtColorsSchoolRegions = this.districtColorsSchoolRegions;
      _.forEach(schoolRegionData, (schoolRegionValue) => {
        if (!districtSchoolRegions[schoolRegionValue.name]) {
          districtSchoolRegions[schoolRegionValue.name] = [];
          districtSchoolRegions[schoolRegionValue.name][
            "officialSchoolName"
          ] = {
            title: schoolRegionValue.name + " School Overall",
            goalTitle: "District Goal",
          };
        }
        districtSchoolRegions[schoolRegionValue.name][String(kpidata.kpiId)] = {
          overall: schoolRegionValue.y,
          overallSuffix: kpidata.dataSuffix,
          overallPrefix: kpidata.dataPrefix,
          overallSubtitle: kpidata.charts.overall.subtitle,
          descriptor: schoolRegionValue.descriptor,
          numerator: schoolRegionValue.numerator,
          denominator: schoolRegionValue.denominator,
          definition: kpidata.definition,
          goal: kpidata.goalText,
          schoolSubtitle: kpidata.charts.topBottomRank.subtitle,
          upIsGood: kpidata.upIsGood,
          goalNumeric: kpidata.goalNumeric,
          roundTo: kpidata.roundTo,
        };

        if (!districtColorsSchoolRegions[schoolRegionValue.name]) {
          districtColorsSchoolRegions[schoolRegionValue.name] = [];
        }
        districtColorsSchoolRegions[schoolRegionValue.name][
          String(kpidata.kpiId)
        ] = schoolRegionValue.color;
      });
    }
  }

  sortColumn(event) {
    let direction = event.direction;
    let col = event.column;
    if (direction === "down") {
      // if the column is currently sorted down, now sort it up
      this.schools = _.sortBy(this.schools, [
        function (school) {
          if (school[col] != null) {
            return school[col].y;
          }
        },
      ]);
      this.sortDirection = "up";
    } else if (direction === "up") {
      // if the column is currently sorted up, now sort it to neutral (alphabetical by school name)
      this.schools = _.sortBy(this.schools, [
        function (school) {
          return school.officialSchoolName;
        },
      ]);
      this.sortDirection = "neutral";
    } else {
      // if the column isn't currently sorted, sort it down
      this.schools = _.sortBy(this.schools, [
        function (school) {
          if (school[col] != null) {
            return -school[col].y;
          }
        },
      ]);
      this.sortDirection = "down";
    }
    this.sortCol = col;
  }

  rerunSort() {
    let sortCol = this.sortCol;
    let sortDirection = this.sortDirection;
    if (sortCol && sortDirection) {
      if (sortDirection == "up") {
        this.schools = _.sortBy(this.schools, [
          function (school) {
            if (school[sortCol] != null) {
              return school[sortCol].y;
            }
          },
        ]);
      } else if (sortDirection == "down") {
        this.schools = _.sortBy(this.schools, [
          function (school) {
            if (school[sortCol] != null) {
              return -school[sortCol].y;
            }
          },
        ]);
      }
    }
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  filterByLevel() {
    if (this.kpiService.selectedSchoolLevel == null) {
      //Reset the filter value
      this.kpiService.selectedSchoolFilter = null;
      this.restoreUnfilteredSchoolList();
    } else {
      this.filterSchoolsAndSetDistrictRowBySchoolLevel();
    }
    this.updateUrlParams();
  }

  filterSchoolsAndSetDistrictRowBySchoolLevel(isInitialLoad: boolean = false) {
    //Filter by selected level
    this.schools = _.filter(this.copyOfSchools, {
      schoolLevel: this.kpiService.selectedSchoolLevel,
    });

    //Set the district data row
    if (
      this.districtSchoolLevels &&
      this.districtSchoolLevels[this.kpiService.selectedSchoolLevel]
    ) {
      this.district = this.districtSchoolLevels[
        this.kpiService.selectedSchoolLevel
      ];
      this.districtColors = this.districtColorsSchoolLevels[
        this.kpiService.selectedSchoolLevel
      ];
    } else if (!isInitialLoad) {
      //Reset the initial district data row
      this.district = this.districtOverall;
      this.districtColors = this.districtColorsOverall;
    }

    //Re-apply the current sort
    this.rerunSort();
  }

  filterByRegion() {
    if (this.kpiService.selectedSchoolRegion == null) {
      //Reset the filter value
      this.kpiService.selectedSchoolFilter = null;
      this.restoreUnfilteredSchoolList();
    } else {
      this.filterSchoolsAndSetDistrictRowBySchoolRegion();
    }
    this.updateUrlParams();
  }

  filterSchoolsAndSetDistrictRowBySchoolRegion(isInitialLoad: boolean = false) {
    //Filter by selected region
    this.schools = _.filter(this.copyOfSchools, {
      schoolRegion: this.kpiService.selectedSchoolRegion,
    });

    //Set the district data row
    if (
      this.districtSchoolRegions &&
      this.districtSchoolRegions[this.kpiService.selectedSchoolRegion]
    ) {
      this.district = this.districtSchoolRegions[
        this.kpiService.selectedSchoolRegion
      ];
      this.districtColors = this.districtColorsSchoolRegions[
        this.kpiService.selectedSchoolRegion
      ];
    } else if (!isInitialLoad) {
      //Reset the initial district data row
      this.district = this.districtOverall;
      this.districtColors = this.districtColorsOverall;
    }

    //Re-apply the current sort
    this.rerunSort();
  }

  filterByDemographic(selectedDemographicCategory) {
    this.kpiService
      .trackFilterBySubcategorySelect(selectedDemographicCategory)
      .subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    if (this.kpiService.selectedSchoolDemographic.id == null) {
      //Reset the filter value
      this.kpiService.selectedSchoolFilter = null;
      this.restoreUnfilteredSchoolList();
    } else {
      //Reset the initial district data row
      this.district = this.districtOverall;
      this.districtColors = this.districtColorsOverall;

      this.getDemoKpiDataForSchools(
        selectedDemographicCategory,
        this.kpiService.selectedSchoolDemographic.text
      );
    }
    this.updateUrlParams();
  }

  restoreUnfilteredSchoolList() {
    //Show the full school list
    this.schools = this.copyOfSchools;

    //Reset the initial district data row
    this.district = this.districtOverall;
    this.districtColors = this.districtColorsOverall;

    //Re-apply the current sort
    this.rerunSort();
  }

  populateSchoolFilterValues(schoolRegions, schoolLevels, demographics) {
    this.schoolFiltersForDropdown = [];
    let schools = this.schools;
    if (schoolRegions && schoolRegions.length > 0) {
      this.schoolRegionsForDropdown = _.map(
        schoolRegions,
        function (schoolRegion: any) {
          let regionCount = _.filter(schools, function (school) {
            if (school["schoolRegion"] == schoolRegion) return school;
          }).length;
          return {
            id: schoolRegion,
            text:
              schoolRegion +
              " (" +
              regionCount +
              (regionCount == 1 ? " school)" : " schools)"),
          };
        }
      );
      //Add placeholder values to the top of each filter dropdown
      if (this.schoolRegionsForDropdown) {
        this.schoolRegionsForDropdown.unshift({
          id: null,
          text: "- No Filter -",
        });
      }
      this.schoolFiltersForDropdown.push({
        id: "school_region",
        text: "School Region",
      });
    }

    if (schoolLevels && schoolLevels.length > 0) {
      this.schoolLevelsForDropdown = _.map(
        schoolLevels,
        function (schoolLevel: any) {
          let levelCount = _.filter(schools, function (school) {
            if (school["schoolLevel"] == schoolLevel) return school;
          }).length;
          return {
            id: schoolLevel,
            text:
              schoolLevel +
              " (" +
              levelCount +
              (levelCount == 1 ? " school)" : " schools)"),
          };
        }
      );
      //Add placeholder values to the top of each filter dropdown
      if (this.schoolLevelsForDropdown) {
        this.schoolLevelsForDropdown.unshift({
          id: null,
          text: "- No Filter -",
        });
      }
      this.schoolFiltersForDropdown.push({
        id: "school_level",
        text: "School Level",
      });
    }

    if (demographics && Object.keys(demographics).length > 0) {
      this.demographicCategoryNamesList = Object.keys(demographics);
      this.demoCategoriesForDropdown = {};
      _.forEach(
        this.demographicCategoryNamesList,
        (demographicCategoryName) => {
          this.schoolFiltersForDropdown.push({
            id: demographicCategoryName,
            text: demographicCategoryName,
          });
          this.demoCategoriesForDropdown[demographicCategoryName] = _.map(
            demographics[demographicCategoryName],
            function (demographicValue: any) {
              return {
                id: demographicValue,
                text: demographicValue,
              };
            }
          );

          if (this.demoCategoriesForDropdown[demographicCategoryName]) {
            this.demoCategoriesForDropdown[demographicCategoryName].unshift({
              id: null,
              text: "- No Filter -",
            });
          }
        }
      );
    }

    //Sort filter names alphabetically
    this.schoolFiltersForDropdown = _.orderBy(
      this.schoolFiltersForDropdown,
      [(filter) => filter.text.toLowerCase()],
      ["asc"]
    );
    this.schoolFiltersForDropdown.unshift({ id: null, text: "- No filter -" });
  }

  showFilter() {
    this.kpiService
      .trackFilterByCategorySelect(this.kpiService.selectedSchoolFilter)
      .subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    //Reset the selected sub-dropdown values
    this.kpiService.selectedSchoolLevel = null;
    this.kpiService.selectedSchoolRegion = null;
    this.kpiService.selectedSchoolDemographic = null;
    this.restoreUnfilteredSchoolList();
    this.updateUrlParams();
  }

  getAllKpiDataForSchools() {
    return this.kpiService.getOurSchoolsKpiData().pipe(
      map(
        (kpidata: SchoolKpiData) => {
          let overall_school_kpis = _.pick(
            kpidata.schools.overall,
            _.keys(this.schools)
          );
          _.merge(this.schools, overall_school_kpis);
        },
        (error) => {
          console.log(error);
        }
      )
    );
  }

  getDemoKpiDataForSchools(category, value) {
    this.kpiService.loadingSchoolData = true;
    let demoschools = _.cloneDeep(this.copyOfSchoolsWithoutKPIs);
    return this.kpiService.getOurSchoolsKpiData().subscribe(
      (kpidata) => {
        let demo_school_kpis = {};
        if (kpidata.schools["demographics"][category]) {
          demo_school_kpis = _.pick(
            kpidata.schools["demographics"][category][value],
            _.keys(demoschools)
          );
        }
        _.merge(demoschools, demo_school_kpis);

        // All chart data has been loaded by school id.  Now sort schools by name.
        demoschools = _.sortBy(demoschools, [
          function (school) {
            return school.officialSchoolName;
          },
        ]);
        this.schools = demoschools;

        //Re-apply the current sort
        this.rerunSort();
        this.kpiService.loadingSchoolData = false;
      },
      (error) => {
        console.log(error);
        this.kpiService.loadingSchoolData = false;
      }
    );
  }

  removeFocusFromDropdown() {
    // removing the focus from the dropdown after item select so the dropdowns on the school and student profile match
    if (this.schoolFiltersDropdown) {
      this.schoolFiltersDropdown.onInputBlur();
    }
    if (this.schoolLevelsDropdown) {
      this.schoolLevelsDropdown.onInputBlur();
    }
    if (this.schoolRegionsDropdown) {
      this.schoolRegionsDropdown.onInputBlur();
    }
    if (this.demoDropdown) {
      this.demoDropdown.onInputBlur();
    }
  }
}
